import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Calculator, Factory, Boxes } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { useAuth, homeFor } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { LOGIN } from "@/constants/testIds";
import { LegalLinks } from "@/components/LegalLinks";

const FEATURES = [
  [Calculator, "Quotes, estimates, and job costing"],
  [Factory, "Shop floor routing and production tracking"],
  [Boxes, "Inventory, procurement, and accounting"],
];

function BrandPanel() {
  return (
    <div className="order-2 lg:order-1 lg:pr-8" data-testid="login-brand-panel">
      <img src="/brand/logo.png" alt="PressOps" className="hidden h-12 w-auto -ml-1 lg:block" />
      <h2 className="mt-0 text-2xl font-bold tracking-tight text-slate-900 lg:mt-10 lg:text-4xl">Sign in to PressOps</h2>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 lg:text-base">Manage quotes, jobs, inventory and finance for your print business in one workspace.</p>
      <ul className="mt-8 space-y-3" data-testid="login-feature-list">
        {FEATURES.map(([Icon, text]) => (
          <li key={text} className="flex items-center gap-3 text-sm text-slate-700">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border bg-white text-[#13294B]" style={{ borderColor: "var(--po-border)" }}><Icon className="h-4 w-4" /></span>
            {text}
          </li>
        ))}
      </ul>
      <p className="mt-10 text-xs text-slate-400">Operated by SpeedPrints Ghana Limited</p>
    </div>
  );
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [help, setHelp] = useState(false);

  if (user) return <Navigate to={homeFor(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      navigate(homeFor(u));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const errCls = error ? "border-rose-400 focus-visible:ring-rose-400" : "";
  return (
    <div className="min-h-screen bg-slate-50 bg-[radial-gradient(circle_at_1px_1px,#E2E8F0_1px,transparent_0)] [background-size:24px_24px]">
      <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-6xl items-center gap-10 px-6 py-10 lg:grid-cols-2 lg:gap-16 lg:px-10">
        <BrandPanel />
        <div className="order-1 flex flex-col items-center lg:order-2">
          <img src="/brand/logo.png" alt="PressOps" className="mb-6 h-11 w-auto lg:hidden" />
          <form onSubmit={submit} className="po-card w-full max-w-md space-y-5 p-6 sm:p-8 fade-up" style={{ boxShadow: "var(--po-shadow-raise)" }} data-testid="login-form" aria-describedby={error ? "login-error" : undefined}>
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-slate-900">Welcome back</h1>
              <p className="mt-1 text-sm text-slate-500">Use your work email to continue.</p>
            </div>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">Email address</span>
              <Input data-testid={LOGIN.emailInput} type="email" autoComplete="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} required aria-invalid={!!error} className={`h-10 ${errCls}`} />
            </label>
            <label className="block space-y-1.5">
              <span className="flex items-center justify-between text-sm font-medium text-slate-700">Password
                <button type="button" data-testid={LOGIN.forgotPasswordLink} onClick={() => setHelp((h) => !h)} className="rounded text-xs font-medium text-[#2B7F77] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Forgot password?</button>
              </span>
              <PasswordInput data-testid={LOGIN.passwordInput} autoComplete="current-password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} required aria-invalid={!!error} className={`h-10 ${errCls}`} />
            </label>
            {help && <p data-testid="login-forgot-help" className="rounded-lg border px-3 py-2 text-xs po-tone-info">Ask your company administrator to reset your password from Users &amp; roles.</p>}
            {error && <p id="login-error" role="alert" data-testid="login-error" className="rounded-lg border px-3 py-2 text-sm po-tone-danger">{error}</p>}
            <Button data-testid={LOGIN.submitButton} type="submit" disabled={busy} className="h-10 w-full bg-[#13294B] hover:bg-[#1c3a66] focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#45BFB4]">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <p className="border-t pt-4 text-xs leading-relaxed text-slate-500" style={{ borderColor: "var(--po-border)" }}>Accounts are created by your company administrator. Print customer? <a href="/portal/login" data-testid="login-portal-link" className="font-medium text-[#2B7F77] underline">Sign in to the client portal</a></p>
            <LegalLinks testId="login-legal-links" className="justify-center" />
          </form>
        </div>
      </div>
      <footer className="border-t bg-white/70 backdrop-blur" style={{ borderColor: "var(--po-border)" }} data-testid="landing-footer">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-2 px-6 py-5 text-xs text-slate-400 sm:flex-row lg:px-10">
          <p>© {new Date().getFullYear()} PressOps · SpeedPrints Ghana Limited</p>
          <LegalLinks testId="landing-footer-links" />
        </div>
      </footer>
    </div>
  );
}
